import { DeviceHub, RemoveCircle } from '@mui/icons-material';
import { Chip, useMediaQuery, useTheme } from '@mui/material';
import type {
  DataGridProProps as DataGridProps,
  GridColDef,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRemoveFromRole } from '~/api/roles';
import { Tooltip } from '~/components/Tooltip';
import { ActionButton } from '~/components/button';
import { SeparatedDataGrid, hiddenHeader } from '~/components/data-grid';
import { DeviceIndicators } from '~/components/devices';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { AuthorizedLink } from '~/components/links';
import { ListCheckbox } from '~/components/list-row';
import { mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useConfirmDialog } from '~/hooks/dialogs';
import ActivateDeviceIllustration from '~/images/illustrations/activate-device.svg';
import { getRuleAccessLabel } from '../../lib';
import type {
  RoleDeviceList__DeviceGroupRule,
  RoleDeviceList__DeviceRule,
  RoleDeviceList__Role,
} from '../RoleDeviceList.generated';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={ActivateDeviceIllustration}
      description={t('pages.roles.devices.empty_state.description')}
      header={t('pages.roles.devices.empty_state.header')}
    />
  );
};

type Row = RoleDeviceList__DeviceRule | RoleDeviceList__DeviceGroupRule;

interface RemoveButtonProps extends GridActionCellProps {
  networkRole: RoleDeviceList__Role;
  row: Row;
}

const RemoveButton = ({ networkRole, row, ...cellProps }: RemoveButtonProps) => {
  const { t } = useTranslation();
  const key = row.__typename === 'DeviceRule' ? 'deviceRuleIds' : 'deviceGroupRuleIds';
  const [removeFromRole, { called, reset }] = useRemoveFromRole({
    variables: { roleId: networkRole.id, [key]: row.id },
  });

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const onClick = async () => {
    if (!(await confirmDelete())) return;
    await removeFromRole();
    reset();
  };

  const deviceName = row.__typename === 'DeviceRule' ? row.device.name : row.deviceGroup.name;

  const prompt =
    row.__typename === 'DeviceRule'
      ? t('pages.roles.dialogs.remove_device.prompt', {
          device_name: deviceName,
          role_name: networkRole.name,
        })
      : t('pages.roles.dialogs.remove_device_group.prompt', {
          device_group_name: deviceName,
          role_name: networkRole.name,
          device_count: row.deviceGroup.deviceCount,
        });

  const title =
    row.__typename === 'DeviceRule'
      ? t('pages.roles.dialogs.remove_device.title')
      : t('pages.roles.dialogs.remove_device_group.title');

  const confirm =
    row.__typename === 'DeviceRule'
      ? t('pages.roles.dialogs.remove_device.confirm')
      : t('pages.roles.dialogs.remove_device_group.confirm');

  return (
    <>
      <ActionButton
        color="error"
        disabled={called}
        Icon={RemoveCircle}
        {...mergeGridActionProps({ onClick }, cellProps)}
        title={t('common.actions.remove')}
      />

      <ConfirmDialog
        {...confirmDeleteProps}
        cancel={t('common.buttons.cancel')}
        confirm={confirm}
        deleteConfirm
        prompt={prompt}
        title={title}
      />
    </>
  );
};

const useColumns = (role: RoleDeviceList__Role): GridColDef<Row>[] => {
  const { t } = useTranslation();
  return useMemo(
    () => [
      {
        align: 'center',
        headerAlign: 'center',
        field: 'type',
        ...hiddenHeader(t('common.labels.type')),
        width: 150,
        renderCell: ({ row }) => {
          return row.__typename === 'DeviceRule' ? (
            <DeviceIndicators device={row.device} />
          ) : (
            <Tooltip describeChild={false} title={t('common.labels.device_group')}>
              <DeviceHub color="primary" titleAccess={t('common.labels.device_group')} />
            </Tooltip>
          );
        },
        sortable: false,
      },
      {
        field: 'name',
        flex: 0.5,
        headerName: t('common.labels.name'),
        renderCell: ({ row }) => (
          <AuthorizedLink
            authorized={
              row.__typename === 'DeviceRule'
                ? row.device.canUpdate.value
                : row.deviceGroup.canUpdate.value
            }
            entity={row.__typename === 'DeviceRule' ? row.device : row.deviceGroup}
          />
        ),
      },
      {
        field: 'access',
        headerName: t('pages.roles.devices.columns.access'),
        renderCell: ({ row }) => getRuleAccessLabel(row.access, t),
      },
      {
        field: 'devices',
        align: 'center',
        headerAlign: 'center',
        headerName: t('common.labels.devices'),
        renderCell: ({ row }) =>
          row.__typename === 'DeviceGroupRule' ? (
            <Chip color="primary" label={row.deviceGroup.deviceCount} />
          ) : (
            '--'
          ),
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: ({ row }) => [<RemoveButton key={0} networkRole={role} row={row} />],
      },
    ],
    [role, t],
  );
};

export interface DeviceTableProps extends Omit<DataGridProps<Row>, 'columns'> {
  loading: boolean;
  role: RoleDeviceList__Role;
}

export const DeviceTable = ({ loading, role, ...props }: DeviceTableProps) => {
  const columns = useColumns(role);

  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      type: !isSmallAndDown,
      devices: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  return (
    <SeparatedDataGrid
      columns={columns}
      columnVisibilityModel={showColumns}
      loading={loading}
      getRowId={(row) => `${row.__typename}:${row.id}`}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      {...props}
    />
  );
};
